/**
 * Patch FoxTheme.pubsub.publish so any subscriber to `cart-update` (including
 * the global header <cart-count>) sees an `item_count` that excludes the
 * package-protection and gift-wrap units. We keep `item_count_raw` on the
 * cart for any code that wants the original count.
 */
(function patchPubSubForCartAddons() {
  if (!window.FoxTheme || !FoxTheme.pubsub || FoxTheme.pubsub.__cartAddonsPatched) return;
  const addons = FoxTheme.cartAddons || {};
  const protectionHandle = addons.packageProtectionEnabled ? addons.packageProtectionHandle : null;
  const giftWrapHandle = addons.giftWrapHandle || null;
  if (!protectionHandle && !giftWrapHandle) return;

  const originalPublish = FoxTheme.pubsub.publish;
  FoxTheme.pubsub.publish = function (eventName, data) {
    if (eventName === FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate && data && data.cart && Array.isArray(data.cart.items)) {
      let deduct = 0;
      if (protectionHandle) {
        const protection = data.cart.items.find((item) => item.handle === protectionHandle);
        if (protection) deduct += protection.quantity;
      }
      if (giftWrapHandle) {
        const giftWrap = data.cart.items.find((item) => item.handle === giftWrapHandle);
        if (giftWrap) deduct += giftWrap.quantity;
      }
      if (deduct > 0) {
        data.cart.item_count_raw = data.cart.item_count;
        data.cart.item_count = Math.max(0, data.cart.item_count - deduct);
      }
    }
    return originalPublish(eventName, data);
  };
  FoxTheme.pubsub.__cartAddonsPatched = true;
})();

/**
 * Cart promotion upsell — handles the "Complete your cart" banner.
 * Action "add" puts a configured product in the cart, optional discount code
 * is applied via /discount/<code>. Action "link" lets the anchor navigate.
 */
class CartPromotionUpsell extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button[data-action="add"]');
    if (this.button) {
      this.button.addEventListener('click', this.onAdd.bind(this));
    }
  }

  async onAdd(event) {
    event.preventDefault();
    if (this.button.getAttribute('aria-disabled') === 'true') return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.button.setAttribute('aria-disabled', 'true');
    this.button.classList.add('btn--loading');

    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: sectionsToBundle,
      });

      const addResp = await fetch(FoxTheme.routes.cart_add_url, {
        ...FoxTheme.utils.fetchConfig('javascript'),
        body,
      });
      const addData = await addResp.json();

      if (addData.status) {
        console.warn('Cart promotion add failed:', addData.description);
        return;
      }

      if (this.dataset.discount) {
        try {
          await fetch(`${FoxTheme.routes.shop_url || ''}/discount/${encodeURIComponent(this.dataset.discount)}?redirect=/cart.js`, {
            method: 'GET',
            credentials: 'same-origin',
          });
        } catch (err) {
          console.warn('Discount apply failed:', err);
        }
      }

      const cartJson = await (
        await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
      ).json();
      cartJson.sections = addData.sections;

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
    } catch (err) {
      console.error('Cart promotion error:', err);
    } finally {
      this.button.removeAttribute('aria-disabled');
      this.button.classList.remove('btn--loading');
    }
  }
}
customElements.define('cart-promotion-upsell', CartPromotionUpsell);

/**
 * Package protection — toggle that maintains a line of N units of a 1¢-priced
 * (or whatever unit price the merchant set) protection product so that the
 * protection cost equals a configured percentage of the cart subtotal.
 *
 * The state (on/off) is persisted as a cart attribute so it survives reloads
 * and is checked by the snippet on render.
 */
class PackageProtection extends HTMLElement {
  static reconciledThisLoad = false;

  constructor() {
    super();
    this.variantId = parseInt(this.dataset.variantId, 10);
    // unitPrice is the protection variant's price in the active presentment
    // currency (cents). If it reads as 0/NaN (e.g. a market where the variant
    // has no resolvable price), the feature is unusable in this currency —
    // never fall back to 1¢, which would compute a runaway quantity.
    this.unitPrice = parseInt(this.dataset.unitPrice, 10);
    this.unitPriceValid = Number.isFinite(this.unitPrice) && this.unitPrice > 0;
    this.percentage = parseFloat(this.dataset.percentage) || 5;
    this.minQuantity = parseInt(this.dataset.minQuantity, 10) || 1;
    this.checkbox = this.querySelector('.package-protection__checkbox');
    this.priceEl = this.querySelector('[data-package-protection-price]');
    this.isActive = this.dataset.active === 'true';
    this.currentQty = parseInt(this.dataset.currentQty, 10) || 0;
    this.requestInFlight = false;

    this.onChange = this.onChange.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);
  }

  connectedCallback() {
    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.onChange);
    }
    document.addEventListener('cart:updated', this.onCartUpdated);

    if (this.isActive && this.unitPriceValid && !PackageProtection.reconciledThisLoad) {
      PackageProtection.reconciledThisLoad = true;
      this.reconcileOnLoad();
    }
  }

  async reconcileOnLoad() {
    try {
      const cart = await fetch(`${FoxTheme.routes.cart_url}.js`, {
        ...FoxTheme.utils.fetchConfig('json', 'GET'),
      }).then((r) => r.json());
      const target = this.computeTargetQty(cart);
      const current = this.currentQtyOf(cart);
      if (target !== current) {
        await this.syncQty(target);
      }
    } catch (err) {
      console.warn('Package protection reconcile failed:', err);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
  }

  /**
   * Compute the qty needed so qty * unitPrice ≈ pct% × (subtotal − protection).
   * Returns 0 if the cart has no other items.
   */
  computeTargetQty(cart) {
    if (!this.unitPriceValid) return 0;
    const subtotalCents = cart.items_subtotal_price || 0;
    let baseCents = subtotalCents;
    cart.items.forEach((item) => {
      if (item.variant_id === this.variantId) {
        baseCents -= item.original_line_price;
      }
    });
    if (baseCents <= 0) return 0;
    const target = Math.round((baseCents * this.percentage) / 100);
    const qty = Math.max(this.minQuantity, Math.ceil(target / this.unitPrice));
    return qty;
  }

  async onChange() {
    this.isActive = !!this.checkbox.checked;
    this.classList.toggle('is-active', this.isActive);
    this.setAttribute('data-active', this.isActive ? 'true' : 'false');
    await this.persistState();

    // Without a valid unit price in this currency we can't size the line.
    // Persist the toggle preference but make no cart change.
    if (!this.unitPriceValid) {
      this.updatePriceLabel(0);
      return;
    }

    const cart = await fetch(`${FoxTheme.routes.cart_url}.js`, {
      ...FoxTheme.utils.fetchConfig('json', 'GET'),
    }).then((r) => r.json());

    const target = this.isActive ? this.computeTargetQty(cart) : 0;
    await this.syncQty(target);
  }

  async onCartUpdated(event) {
    if (this.requestInFlight) return;
    if (!this.isActive || !this.unitPriceValid) return;
    const cart = event.detail && event.detail.cart;
    if (!cart) return;
    const target = this.computeTargetQty(cart);
    const current = this.currentQtyOf(cart);
    if (target !== current) {
      await this.syncQty(target);
    } else {
      this.updatePriceLabel(target * this.unitPrice);
    }
  }

  currentQtyOf(cart) {
    const item = cart.items.find((i) => i.variant_id === this.variantId);
    return item ? item.quantity : 0;
  }

  updatePriceLabel(amountCents) {
    if (!this.priceEl) return;
    if (amountCents > 0) {
      const formatted = FoxTheme.Currency
        ? FoxTheme.Currency.formatMoney(amountCents, FoxTheme.settings.moneyFormat)
        : `$${(amountCents / 100).toFixed(2)}`;
      this.priceEl.innerHTML = formatted;
    } else {
      this.priceEl.textContent = '—';
    }
  }

  async persistState() {
    const body = JSON.stringify({
      attributes: { '_package-protection': this.isActive ? 'on' : 'off' },
    });
    try {
      await fetch(FoxTheme.routes.cart_update_url, {
        ...FoxTheme.utils.fetchConfig(),
        body,
      });
    } catch (err) {
      console.warn('Package protection state persist failed:', err);
    }
  }

  async syncQty(targetQty) {
    this.requestInFlight = true;
    this.classList.add('is-loading');
    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        updates: { [this.variantId]: targetQty },
        sections: sectionsToBundle,
      });

      const resp = await fetch(FoxTheme.routes.cart_update_url, {
        ...FoxTheme.utils.fetchConfig(),
        body,
      });
      const data = await resp.json();

      const item = (data.items || []).find((i) => i.variant_id === this.variantId);
      const lineTotal = item ? item.original_line_price : 0;
      this.updatePriceLabel(lineTotal);

      data.sections = data.sections || {};
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: data });
    } catch (err) {
      console.error('Package protection sync error:', err);
    } finally {
      this.requestInFlight = false;
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('package-protection', PackageProtection);

/**
 * Cart gift wrap — toggle + quantity selector. The line item is hidden from
 * the cart list (filter is in the cart templates) and surfaces only at
 * checkout. Quantity is shoppable while the toggle is on; flipping the
 * toggle off removes the line entirely.
 */
class CartGiftWrap extends HTMLElement {
  static MAX_QTY = 99;

  constructor() {
    super();
    this.variantId = parseInt(this.dataset.variantId, 10);
    this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
    this.checkbox = this.querySelector('.cart-gift-wrap__checkbox');
    this.qtyControl = this.querySelector('[data-cart-gift-wrap-qty-control]');
    this.qtyValueEl = this.querySelector('[data-cart-gift-wrap-qty]');
    this.priceEl = this.querySelector('[data-cart-gift-wrap-price]');
    this.isActive = this.dataset.active === 'true';
    this.qty = parseInt(this.dataset.currentQty, 10) || (this.isActive ? 1 : 1);
    if (this.qtyValueEl) this.qtyValueEl.textContent = String(this.qty);
    this.requestInFlight = false;

    this.onChange = this.onChange.bind(this);
    this.onQtyClick = this.onQtyClick.bind(this);
  }

  connectedCallback() {
    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.onChange);
    }
    if (this.qtyControl) {
      this.qtyControl.addEventListener('click', this.onQtyClick);
    }
  }

  onChange() {
    this.isActive = !!this.checkbox.checked;
    this.classList.toggle('is-active', this.isActive);
    this.setAttribute('data-active', this.isActive ? 'true' : 'false');
    if (this.qtyControl) {
      if (this.isActive) {
        this.qtyControl.removeAttribute('hidden');
      } else {
        this.qtyControl.setAttribute('hidden', '');
      }
    }
    if (this.isActive && this.qty < 1) this.qty = 1;
    const targetQty = this.isActive ? this.qty : 0;
    this.updatePriceLabel(targetQty * this.unitPrice);
    this.scheduleSync(targetQty);
  }

  onQtyClick(event) {
    const btn = event.target.closest('[data-gift-wrap-qty-action]');
    if (!btn || !this.isActive) return;
    const action = btn.dataset.giftWrapQtyAction;
    const next = action === 'increment' ? this.qty + 1 : this.qty - 1;
    if (next < 1 || next > CartGiftWrap.MAX_QTY) return;
    this.qty = next;
    if (this.qtyValueEl) this.qtyValueEl.textContent = String(this.qty);
    this.updatePriceLabel(this.qty * this.unitPrice);
    this.scheduleSync(this.qty);
  }

  scheduleSync(targetQty) {
    this.pendingQty = targetQty;
    if (this.requestInFlight) return;
    queueMicrotask(() => this.drainSync());
  }

  async drainSync() {
    while (this.pendingQty != null) {
      const next = this.pendingQty;
      this.pendingQty = null;
      await this.syncQty(next);
    }
  }

  updatePriceLabel(amountCents) {
    if (!this.priceEl) return;
    const formatted = FoxTheme.Currency
      ? FoxTheme.Currency.formatMoney(amountCents, FoxTheme.settings.moneyFormat)
      : `$${(amountCents / 100).toFixed(2)}`;
    this.priceEl.innerHTML = formatted;
  }

  async syncQty(targetQty) {
    this.requestInFlight = true;
    this.classList.add('is-loading');
    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        updates: { [this.variantId]: targetQty },
        sections: sectionsToBundle,
      });

      const resp = await fetch(FoxTheme.routes.cart_update_url, {
        ...FoxTheme.utils.fetchConfig(),
        body,
      });
      const data = await resp.json();

      data.sections = data.sections || {};
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: data });
    } catch (err) {
      console.error('Cart gift wrap sync error:', err);
    } finally {
      this.requestInFlight = false;
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('cart-gift-wrap', CartGiftWrap);
